import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/contexts/AuthContext";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight, KeyRound, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw1.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw1 !== pw2) return toast.error("Passwords don't match");
    if (!token) return toast.error("Reset link is missing its token");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw1 });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => nav("/login"), 1500);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div className="hidden lg:block relative bg-[#0B132B] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B132B] via-[#0B132B]/95 to-[#1C2541]/70" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <BrandLogo size="md" variant="light" />
          <div className="max-w-md">
            <div className="inline-block px-2 py-1 rounded-sm bg-[#D4AF37] text-[#0B132B] text-[10px] uppercase tracking-[0.25em] font-bold mb-6">
              New password
            </div>
            <h1 className="font-display text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.05]">
              Choose something you'll remember.
            </h1>
            <p className="text-white/70 text-base mt-6 leading-relaxed">
              Pick a strong password. Minimum 6 characters. You'll be sent back to sign in as soon as it's saved.
            </p>
          </div>
          <div className="text-xs text-white/40 tracking-[0.18em] uppercase">
            © Advantage Warranty Group
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-12">
        {done ? (
          <div className="w-full max-w-sm space-y-6 text-center" data-testid="reset-done-panel">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 grid place-items-center">
              <CheckCircle2 size={30} className="text-emerald-700" />
            </div>
            <div>
              <h2 className="font-display text-3xl font-black text-[#0B132B] mt-2">Password updated</h2>
              <p className="text-sm text-[#6C757D] mt-3">Redirecting to sign in…</p>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="reset-form">
            <div className="lg:hidden"><BrandLogo size="md" /></div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">Reset password</div>
              <h2 className="font-display text-3xl font-black text-[#0B132B] mt-2">Set a new password</h2>
              <p className="text-sm text-[#6C757D] mt-2 leading-relaxed">Minimum 6 characters.</p>
            </div>
            {!token && (
              <div className="text-sm text-red-600" data-testid="reset-missing-token">
                This link is missing its token. Please request a new reset email.
              </div>
            )}
            <div className="space-y-4">
              <div>
                <Label htmlFor="rp-pw1" className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B]">New password</Label>
                <Input
                  id="rp-pw1"
                  type="password"
                  required
                  value={pw1}
                  onChange={(e) => setPw1(e.target.value)}
                  className="mt-2 h-11 rounded-sm"
                  data-testid="reset-password-input"
                />
              </div>
              <div>
                <Label htmlFor="rp-pw2" className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B]">Confirm password</Label>
                <Input
                  id="rp-pw2"
                  type="password"
                  required
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  className="mt-2 h-11 rounded-sm"
                  data-testid="reset-confirm-input"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading || !token}
              className="w-full h-11 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white font-semibold"
              data-testid="reset-submit-button"
            >
              <KeyRound size={16} className="mr-2" />
              {loading ? "Updating…" : "Update password"}
            </Button>
            <p className="text-sm text-[#6C757D]">
              <Link to="/login" className="text-[#0B132B] font-semibold hover:text-[#D4AF37]" data-testid="reset-back-to-login">
                Back to sign in <ArrowRight size={14} className="inline ml-1" />
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
